import { ApplicantNameCell } from './ApplicantNameCell';
import { ViewMorePopover } from '@/components/ats/ViewMorePopover/ViewMorePopover';
import { secondsToHumanTime } from '@/pages/reports/non-ats-reports/ats/table';
import { KImage, KText } from '@/ui-library/components';
import { Flex, Link } from '@chakra-ui/react';

export const NameCell = (row) => {
  const atsApplication = row?.original?.ats_application;
  const { ats_job = {} } = row?.original?.job ?? {};
  const { id: jobId } = ats_job ?? {};

  return <ApplicantNameCell atsApplication={atsApplication} jobId={jobId} />;
};

export const SourceName = (row) => {
  const { ats_candidate_source = {} } = row?.original?.source ?? {};
  const { name } = ats_candidate_source ?? {};

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {name || '-'}
      </KText>
    </Flex>
  );
};

export const Jobname = (row) => {
  const { ats_job = {} } = row?.original?.job ?? {};
  const { name, id: jobId } = ats_job ?? {};
  return (
    <Link href={`/jobs/${jobId}`} target="_blank" w="100%">
      <KText
        maxW="100%"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
        _hover={{
          textDecoration: 'underline',
          color: 'primary-500',
          cursor: 'pointer',
        }}
      >
        {name || '-'}
      </KText>
    </Link>
  );
};

export const TimeToReject = (row) => {
  const duration = row?.original?.time_to_reject ?? 0;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {secondsToHumanTime(duration)}
    </KText>
  );
};

export const RejectionType = (row) => {
  const { rejection_type = '' } = row?.original ?? {};
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {rejection_type || '-'}
    </KText>
  );
};

export const RejectionReason = (row) => {
  const { ats_rejection_reason = {} } = row?.original?.rejection_reason ?? {};
  const { reason } = ats_rejection_reason ?? {};
  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {reason || '-'}
      </KText>
    </Flex>
  );
};

export const CreditedTo = (row) => {
  const { credited_to } = row?.original ?? {};
  const name =
    typeof credited_to === 'object' && credited_to?.name
      ? credited_to.name
      : typeof credited_to === 'string'
        ? credited_to
        : '';
  const avatar =
    typeof credited_to === 'object' ? credited_to?.avatar : undefined;

  return (
    <>
      {name ? (
        <Flex w="100%" alignItems="center" gap="6px">
          <KImage
            name={name}
            src={avatar}
            backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
          />
          <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
            {name}
          </KText>
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};

export const RejectedBy = (row) => {
  const { rejected_by } = row?.original ?? {};
  const name =
    typeof rejected_by === 'object' && rejected_by?.name
      ? rejected_by.name
      : typeof rejected_by === 'string'
        ? rejected_by
        : '';
  const avatar =
    typeof rejected_by === 'object' ? rejected_by?.avatar : undefined;

  return (
    <>
      {name ? (
        <Flex w="100%" alignItems="center" gap="6px">
          <KImage
            name={name}
            src={avatar}
            backgroundStyles={{ minWidth: '20px', minHeight: '20px' }}
          />
          <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
            {name}
          </KText>
        </Flex>
      ) : (
        '-'
      )}
    </>
  );
};

export const RejectionStage = (row) => {
  const { rejection_stage = '' } = row?.original ?? {};
  const name =
    typeof rejection_stage === 'object'
      ? (rejection_stage?.name ?? rejection_stage?.stage_name ?? '')
      : (rejection_stage ?? '');

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {name || '-'}
      </KText>
    </Flex>
  );
};

const DepartmentName = (row) => {
  const { ats_department = {} } = row?.original?.department ?? {};
  const { name } = ats_department ?? {};
  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {name || '-'}
      </KText>
    </Flex>
  );
};

const parseOfficeNames = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item && typeof item === 'object' && 'ats_office' in item) {
          const office = (item as { ats_office?: { name?: string } })
            .ats_office;
          return office?.name ?? '';
        }
        return String(item);
      })
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed
            .map((item: unknown) =>
              item && typeof item === 'object' && 'ats_office' in item
                ? ((item as { ats_office?: { name?: string } }).ats_office
                    ?.name ?? '')
                : String(item)
            )
            .filter(Boolean)
        : [];
    } catch {
      return value.trim() ? [value.trim()] : [];
    }
  }
  return [];
};

const renderOfficeInitial = (items: string[]) => (
  <Flex w="100%" gap="6px" alignItems="center">
    {items?.map((name, i) => (
      <KText
        key={i}
        maxW="130px"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
      >
        {name}
      </KText>
    ))}
  </Flex>
);

const renderOfficeOthers = (list: string[]) => (
  <Flex flexDir="column" gap=".25em">
    {list?.map((name, i) => (
      <Flex key={i} alignItems="center" gap=".25em">
        <KText
          fontSize="sm"
          color="secondary-700"
          isTruncated
          enableTooltip={true}
        >
          {name}
        </KText>
      </Flex>
    ))}
  </Flex>
);

export const OfficeNames = (row) => {
  const { office_names } = row?.original ?? {};
  const offices = parseOfficeNames(office_names);

  return (
    <Flex w="100%">
      {offices?.length > 0 ? (
        <ViewMorePopover
          list={offices}
          max={1}
          type="officeList"
          renderInitial={renderOfficeInitial}
          renderOthers={renderOfficeOthers}
          emptyMsg="-"
          trigger="hover"
        />
      ) : (
        <KText size="sm-regular">-</KText>
      )}
    </Flex>
  );
};

export { DepartmentName };
